export const CHAR_DASHA_HEADLINES = {
  en: { title: "Free Char Dasha", accent: "Calculator" },
  hi: { title: "चर दशा", accent: "कैलकुलेटर" },
} as const;
export function charDashaHeadline(language: "en" | "hi") {
  const copy = CHAR_DASHA_HEADLINES[language];
  return `${copy.title} ${copy.accent}`;
}
type ContentSection = {
  title: string;
  paragraphs: Array<string | { label: string; body: string }>;
};

type FaqItem = {
  question: string;
  answer: string;
};

const CONTENT_SECTIONS: ContentSection[] = [
  {
    title: "What is Char Dasha (Jaimini Astrology)?",
    paragraphs: [
      "Char Dasha — also called Chara Dasha or Rashi Dasha — is the primary dasha system of Jaimini astrology, one of the two great schools of Vedic Jyotish. While the more widely known Vimshottari system assigns periods to the nine planets (navagraha), Char Dasha assigns time periods to the twelve rashis (zodiac signs) themselves. This fundamental difference makes Char Dasha uniquely suited to understanding life events through the lens of environmental circumstances, geographic changes, and the sign-level themes that define each life chapter.",
      "The system originates from the Jaimini Sutras, an ancient text attributed to Maharishi Jaimini — a disciple of Maharishi Parashara. Where Parashara's system governs the planetary period tradition most astrologers follow, Jaimini created an independent framework: sign-based periods, special karakas (planetary significators), and unique aspects called Jaimini aspects that differ entirely from Parashari aspects. Char Dasha is its centrepiece.",
      "The Ishvaram Char Dasha calculator derives your complete rashi dasha sequence from your birth chart using Swiss Ephemeris for precise planetary positions, then applies the Jaimini Sutra rules for period duration: the years of each sign's dasha equals the distance (in signs) of its lord from the sign itself — counting forward for odd-footed signs, backward for even-footed signs. For dual-lorded signs (Vrishchik, Kumbh, Meen), the stronger lord between the traditional ruler and the Jaimini co-lord determines the period length.",
    ],
  },
  {
    title: "How Char Dasha Differs from Vimshottari Dasha",
    paragraphs: [
      {
        label: "Basis",
        body: "Vimshottari uses the Moon's nakshatra to determine the starting dasha lord (a planet). Char Dasha uses the Lagna (ascendant sign) to determine the starting dasha sign (a rashi). Both systems cover 12 periods, but Vimshottari's repeat in a fixed 120-year cycle, while Char Dasha periods vary in duration from 1 to 12 years based on individual chart geometry.",
      },
      {
        label: "Direction",
        body: "Whether Char Dasha periods run forward or backward through the zodiac depends on whether the Lagna is an odd-footed sign (Mesh, Vrishabh, Mithun, Tula, Vrishchik, Dhanu — forward) or an even-footed sign (Kark, Singh, Kanya, Makar, Kumbh, Meen — backward). This directional rule creates entirely unique sequences even for people born on the same day.",
      },
      {
        label: "Karakas",
        body: "Jaimini's most significant innovation is the concept of Chara Karakas — seven planetary significators determined dynamically from each birth chart. The planet with the highest degree within its sign becomes the Atmakaraka (soul significator). The planet with the second-highest degree becomes the Amatyakaraka (career significator). This ranking creates a chart-specific hierarchy that Parashara's system does not use.",
      },
      {
        label: "Prediction style",
        body: "Vimshottari excels at timing psychological and internal shifts. Char Dasha is particularly strong for timing external events — geographic moves, career domain changes, marriage, major financial transitions. Jaimini astrologers often use both systems together: Vimshottari for internal themes, Char Dasha for external manifestation.",
      },
    ],
  },
  {
    title: "Atmakaraka — The Soul Significator in Jaimini Astrology",
    paragraphs: [
      "The Atmakaraka is the cornerstone of Jaimini astrology. Every planet moves through the zodiac at its own speed and at any given moment occupies a specific degree within a sign (0 to 29.99 degrees). The planet that has traveled furthest within its current sign — closest to completing it — becomes the Atmakaraka. In Jaimini's view, this planet carries the most advanced karmic agenda: it has experienced the most of its current sign and is therefore most qualified to represent the soul's core lesson.",
      "The Atmakaraka's placement in the navamsa chart (D-9) reveals what the soul craves most deeply. Its placement in the birth chart (D-1) shows where that craving plays out in real life. Jaimini astrologers read all other chart factors relative to the Atmakaraka: which house does it aspect? Is it afflicted or supported? Where does the karakamsha (Atmakaraka's navamsa sign placed in the birth chart) fall?",
      "The six other karakas — Amatyakaraka (career), Bhratrukaraka (siblings), Matrukaraka (mother), Pitrukaraka (father), Gnatikaraka (competition), and Darakaraka (spouse) — complete the Jaimini significator hierarchy. Unlike the fixed karaka assignments in Parashara's system (where Jupiter is always the karaka for children, for example), Jaimini's karakas shift from chart to chart, making every reading genuinely individual.",
    ],
  },
  {
    title: "Reading Your Char Dasha — A Practical Guide",
    paragraphs: [
      "The first step is to identify your current Char Dasha period. The active rashi (sign) sets the environmental backdrop of your life right now. Events and opportunities will tend to align with that sign's significations: Mesh for bold action and beginnings; Kark for home and emotional matters; Dhanu for learning, travel, and dharmic pursuits.",
      "The second step is to examine the sign's lord — the planet ruling the active dasha sign — in your birth chart. The lord's strength, placement, and aspects determine whether the active sign period delivers its significations easily or with obstacles. A strong, unafflicted lord in a benefic house will give smooth results; a debilitated lord in a dusthana will create turbulence.",
      "The third step is to check whether your Atmakaraka or Amatyakaraka is linked to the active dasha sign — through placement, aspect, or ownership. When the Atmakaraka connects to the active dasha sign, the period takes on a soul-level intensity. Career pivots, spiritual awakenings, and life-direction corrections often coincide with these conjunctions.",
    ],
  },
];

const FAQS: FaqItem[] = [
  {
    question: "What is Char Dasha in Jaimini astrology?",
    answer:
      "Char Dasha (also called Chara Dasha or Rashi Dasha) is the signature dasha system of Jaimini astrology. Unlike Vimshottari, which assigns periods to the nine planets, Char Dasha assigns periods to the twelve signs (rashis). The duration of each sign's dasha equals the distance of its lord from the sign, per rules in the Jaimini Sutras (Adhyaya 2). It is particularly effective for timing external life events such as geographic moves, marriage, career shifts, and financial transitions.",
  },
  {
    question: "How is Char Dasha calculated?",
    answer:
      "The starting sign is the Lagna (ascendant). Whether periods progress forward or backward depends on the Lagna sign: odd-footed signs (Mesh, Vrishabh, Mithun, Tula, Vrishchik, Dhanu) count forward; even-footed signs (Kark, Singh, Kanya, Makar, Kumbh, Meen) count backward. The duration of each sign's dasha equals the number of signs from the sign to its lord (inclusive), in the appropriate direction. For dual-lorded signs (Vrishchik, Kumbh, Meen), the stronger of the two lords determines the period length.",
  },
  {
    question: "What is the Atmakaraka and why does it matter?",
    answer:
      "The Atmakaraka is the planet with the highest degree within its sign in your birth chart. In Jaimini astrology, it is the soul significator — the planet that represents the core karmic lesson of this lifetime. All Jaimini predictions are read relative to the Atmakaraka's strength and placement. Its position in the navamsa reveals the soul's deepest desire; its placement in the birth chart shows where that desire plays out. It is the single most important planet in Jaimini astrology.",
  },
  {
    question: "How many Char Dasha periods are there?",
    answer:
      "There are 12 Char Dasha periods — one for each rashi (zodiac sign). Unlike Vimshottari's fixed 120-year total, Char Dasha's total duration varies by chart because each sign's period length is individually calculated. The minimum for any sign is 1 year; the maximum is 12 years. The sum of all 12 periods can range from 12 to 144 years, but most charts produce a total of around 90-120 years.",
  },
  {
    question: "Is birth time required for Char Dasha?",
    answer:
      "Yes, birth time is important for Char Dasha because the Lagna (ascendant) changes approximately every two hours. Since the Lagna determines the starting sign and direction of the entire dasha sequence, an incorrect Lagna will produce a completely wrong sequence. Unlike Vimshottari (which uses the Moon — slow-moving enough that an approximate time sometimes works), Char Dasha requires accurate birth time for reliable results. Even a 30-minute error can shift the Lagna sign.",
  },
  {
    question: "What is the difference between Char Dasha and Vimshottari Dasha?",
    answer:
      "Vimshottari Dasha assigns periods to the nine planets and starts from the Moon's nakshatra. Char Dasha assigns periods to the twelve signs (rashis) and starts from the Lagna. Vimshottari is better for internal, psychological, and health-related timing; Char Dasha excels at timing external events — location changes, marriage, career domain shifts. Experienced Jaimini astrologers use both together: Vimshottari for the internal theme, Char Dasha for external manifestation. Both must align to predict a significant event with confidence.",
  },
  {
    question: "What are the Jaimini karakas?",
    answer:
      "Jaimini defines seven Chara Karakas (variable significators) ranked by each planet's degree within its sign: Atmakaraka (soul, highest degree), Amatyakaraka (career/mind), Bhratrukaraka (siblings), Matrukaraka (mother), Pitrukaraka (father/guru), Gnatikaraka (enemies/competition), and Darakaraka (spouse, lowest degree among the seven). Unlike Parashara's fixed karakas (Jupiter always for children, etc.), Jaimini's karakas change from chart to chart, making the system highly individual.",
  },
];

export function CharDashaSeoContent() {
  return (
    <section
      className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16"
      style={{ color: "var(--ish-text-muted)" }}
    >
      {CONTENT_SECTIONS.map((section, index) => (
        <SeoSection key={section.title} section={section} first={index === 0} />
      ))}
      <FaqSection />
    </section>
  );
}

function SeoSection({ section, first }: { section: ContentSection; first: boolean }) {
  return (
    <>
      <h2
        className={`text-xl font-bold mb-4 ${first ? "" : "mt-10"}`}
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        {section.title}
      </h2>
      {section.paragraphs.map((paragraph, index) => (
        <SeoParagraph
          key={index}
          paragraph={paragraph}
          last={index === section.paragraphs.length - 1}
        />
      ))}
    </>
  );
}

function SeoParagraph({
  paragraph,
  last,
}: {
  paragraph: ContentSection["paragraphs"][number];
  last: boolean;
}) {
  if (typeof paragraph === "string") {
    return <p className={`text-body leading-relaxed ${last ? "mb-6" : "mb-4"}`}>{paragraph}</p>;
  }

  return (
    <p className={`text-body leading-relaxed ${last ? "mb-6" : "mb-4"}`}>
      <strong style={{ color: "var(--ish-dark)" }}>{paragraph.label}:</strong> {paragraph.body}
    </p>
  );
}

function FaqSection() {
  return (
    <>
      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
        {FAQS.map((faq) => (
          <details key={faq.question} className="group">
            <summary
              className="text-body font-semibold cursor-pointer"
              style={{ color: "var(--ish-dark)" }}
            >
              {faq.question}
            </summary>
            <p className="mt-2 text-body-small leading-relaxed pl-4">{faq.answer}</p>
          </details>
        ))}
      </div>
    </>
  );
}
